// Thin wrapper over the Vatar Games SDK (window.Vatar, loaded by vatar.html).
// The rest of NameIt! talks to this file, never to window.Vatar directly.

const sdk = () => (typeof window !== 'undefined' ? window.Vatar : undefined);

export const hasVatarSdk = () => Boolean(sdk());

// Resolves { player: { id, name, avatar }, session, mode }. Rejects with
// code "not_framed" when the page is opened outside the Lagos Life frame.
export function initVatar() {
  if (!sdk()) return Promise.reject(Object.assign(new Error('Vatar SDK missing'), { code: 'not_framed' }));
  return sdk().init();
}

// A fresh signed token (valid 15 minutes). Fetch one for every server request.
export async function freshSession() {
  const { session } = await sdk().session();
  return session;
}

// Opens Vatar's own pay sheet. Resolves { purchaseId, productId } once Vatar's
// server has confirmed payment. Rejects with code: cancelled, payment_failed,
// busy, rate_limited, not_for_sale, owned, ...
export function purchase(productId) {
  return sdk().purchase(productId);
}

// Paid but not yet delivered: recover after a reload or crash.
export async function pendingPurchases() {
  return (await sdk()?.purchases?.()) ?? [];
}

// Payments confirmed while no purchase() call was waiting.
export function onPurchase(fn) {
  return sdk()?.onPurchase?.(fn) ?? (() => {});
}

// Stop timers and sound while the app is backgrounded or the pay sheet is open.
export function onVatarPause(fn) {
  return sdk()?.onPause?.(fn) ?? (() => {});
}
export function onVatarResume(fn) {
  return sdk()?.onResume?.(fn) ?? (() => {});
}

// Scores: whole numbers, the player's best is kept, and Vatar accepts one
// score per player every 20 seconds. We hold the highest pending value and
// send it when the window opens.
const SCORE_GAP_MS = 20500;
let lastSentAt = 0;
let pendingScore = null;
let timer = null;

async function flushScore() {
  timer = null;
  if (pendingScore == null || !sdk()) return;
  const value = pendingScore;
  pendingScore = null;
  lastSentAt = Date.now();
  try {
    await sdk().submitScore(value);
  } catch (err) {
    // too_soon: another tab or an earlier call used the window. Try once more.
    if (err?.code === 'too_soon') {
      pendingScore = Math.max(pendingScore ?? 0, value);
      timer = setTimeout(flushScore, SCORE_GAP_MS);
    }
  }
}

export function postScore(score) {
  const value = Math.floor(Number(score));
  if (!sdk() || !Number.isFinite(value) || value <= 0) return;
  pendingScore = Math.max(pendingScore ?? 0, value);
  if (timer) return;
  const wait = Math.max(0, lastSentAt + SCORE_GAP_MS - Date.now());
  timer = setTimeout(flushScore, wait);
}

// { top: [{ rank, name, score }], me: { rank, label, score } | null }
export function fetchLeaderboard() {
  return sdk().leaderboard();
}
